/**
 * The studio's book, read once for the two things this page needs off it: a client's
 * name, and the list of clients a dry run can be asked about.
 *
 * **The names exist here because the money page's own read models do not carry one.**
 * `MoneyRunRowSchema`, `MoneyLedgerRowSchema`, `FailedPaymentSchema` and
 * `UnmatchedPaymentSchema` all name their client by `ClientSlugSchema` alone, and
 * `mockups/studio/money.html` prints "Vela Interiors" in all four tables — so
 * something has to turn one into the other. `clients_summary` carries `{ client, name }`
 * for every client that has ever had a retainer and, since October 2026, every open
 * account that has not — so a one-off client's invoice is named here too, where it used
 * to print its slug.
 *
 * It is a **lookup, not a join**: nothing reads a second field off that row, and an
 * unknown slug renders as the slug rather than as a blank. The session report asks for
 * `name` on the four row schemas, at which point half of this file goes away.
 *
 * **The list is why it is not only names.** `Run billing (dry)` has to offer a client
 * to run, and the obvious source — this period's run table — is *empty on exactly the
 * month somebody would want to preview*, which is a month nothing has been billed for
 * yet. The book is the list that is right in both cases.
 */

import { useStudioRead } from '../../../api/studio.js'

/** The company for a slug, or the slug itself while the book is still loading. */
export type NameOf = (client: string) => string

export type ClientBook = {
  readonly nameOf: NameOf
  /**
   * Every client that has ever had a retainer, by slug, in the book's own order — the
   * clients a dry run or a first run is offered for, and the whole book until October
   * 2026. Not its no-retainer rows: `billing/queries.ts` `staffClientsToBill` keeps a
   * client that never had a retainer off the monthly run, and a run is a record — one
   * started by hand for such a client would answer every later run of that month.
   */
  readonly clients: readonly string[]
}

export function useClientBook(): ClientBook {
  /*
   * `status: 'all'` — cancelled clients included. A cancelled client still has last
   * month's invoice in the ledger and last month's failed charge in the queue, and
   * printing their slug on the one page that is about money owed would be the worst
   * place to lose a name.
   */
  const book = useStudioRead('clients_summary', { status: 'all' }, { inline: true })
  const rows = book.data?.rows ?? []

  return {
    nameOf: (client) => rows.find((one) => one.client === client)?.name ?? client,
    clients: rows.filter((one) => one.retainer_status !== null).map((one) => one.client),
  }
}
